import { VdaType } from '@src/quince-site-common/src/domains/svd/svd-product.types'
import type { SvdVdaValue } from '@src/quince-site-common/src/domains/svd/svd-product.types'
import { ColorOption } from '@src/quince-site-pdp/src/atoms/optionSelector/colorOption/ColorOption'
import { ImageOption } from '@src/quince-site-pdp/src/atoms/optionSelector/imageOption/ImageOption'
import { TextOption } from '@src/quince-site-pdp/src/atoms/optionSelector/textOption/TextOption'

import type { FC } from 'react'

/**
 * SVD analog of the legacy `OptionSelector` renderer architecture (see
 * `quince-site-pdp/.../optionSelector/OptionSelector.constant.ts`): a common prop contract + a
 * type → renderer map, so `SvdOptionSelector` dispatches by VDA `type` the same way the legacy selector
 * dispatches by `ProductOptionValue.displayType`.
 *
 * The SVD difference: the render key is the **VDA `type`** (SWATCH / MEASUREMENT / SELECT / IMAGE), not a
 * per-value `displayType`. Each renderer is a thin adapter that maps an `SvdVdaValue` onto the *shared*
 * legacy leaf primitive (`ColorOption` / `TextOption` / `ImageOption`), so styling stays identical to
 * today. The value `key` is the selection identity (radio value); `localisedValue` is the label.
 *
 * To support a new VDA type: add a renderer here and one entry to `VDA_TYPE_VS_OPTION_RENDERER`.
 */

/** Common contract every SVD option renderer receives — analog of the legacy `OptionProp`. */
export interface SvdOptionProp {
  value: SvdVdaValue
  isSelected: boolean
  selectionSetId: string
  onChange: (valueKey: string) => void
  className?: string
  isRound?: boolean
  /** On-sale red-dot under the swatch (primary-swatch only; every variant of this value is on promo). */
  showRedDot?: boolean
}

/**
 * SWATCH — always renders round (invariant): a HEX_COLOR swatch as a round color dot (`ColorOption`'s
 * `.colorDot` is inherently `border-radius: 50%`); an IMAGE swatch as a round image chip (`isRound`).
 * Non-swatch VDA types own their own shape via their renderer (pill for MEASUREMENT/SELECT, square for
 * IMAGE) — so roundness is a per-renderer concern here, not a selector-level flag like legacy's
 * `isAllRoundOptions`.
 */
const SwatchOption: FC<SvdOptionProp> = props => {
  const { value, isSelected, selectionSetId, onChange, className, showRedDot } = props
  if (value.swatchType === 'IMAGE' && value.swatchImageUrl) {
    return (
      <ImageOption
        value={value.key}
        displayName={value.localisedValue}
        imgURL={value.swatchImageUrl}
        isSelected={isSelected}
        selectionSetId={selectionSetId}
        onChange={onChange}
        className={className}
        isRound // SWATCH is always round
      />
    )
  } else if (value.swatchType === 'HEX_COLOR' && value.hexCode) {
    return (
      <ColorOption
        value={value.key}
        displayName={value.localisedValue}
        hexCode={value.hexCode}
        isSelected={isSelected}
        addborder={value.addBorder}
        selectionSetId={selectionSetId}
        onChange={onChange}
        className={className}
        showRedDot={showRedDot}
      />
    )
  } else {
    return <PillOption {...props} />
  }
}

/** MEASUREMENT / SELECT — a text pill. */
const PillOption: FC<SvdOptionProp> = ({ value, isSelected, selectionSetId, onChange, className }) => (
  <TextOption
    value={value.key}
    displayName={value.localisedValue}
    isSelected={isSelected}
    selectionSetId={selectionSetId}
    onChange={onChange}
    className={className}
  />
)

/** VDA `type` → renderer (analog of `DISPLAY_TYPE_VS_OPTION_RENDERER`). */
export const VDA_TYPE_VS_OPTION_RENDERER: Record<VdaType, FC<SvdOptionProp>> = {
  [VdaType.SWATCH]: SwatchOption,
  [VdaType.MEASUREMENT]: PillOption,
  [VdaType.SELECT]: PillOption,
}

// No fallback renderer by design — parity with legacy, which drops values whose type has no renderer.
// `SvdOptionSelector` renders nothing for a VDA whose `type` isn't in this map (see its comment).
